import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Container } from '../components/Animations'
import ProgressIndicator from '../components/ProgressIndicator'
import { Chevron } from '../icons/Chevron'
import NavbarController from '../state/NavbarController'
import Radom, { AccountServicesType, BusinessAddress, BusinessBasics as BusinessBasicsTypes, BusinessTypes, IAccountUsage, IVerification, LegalAndTaxDetails, Ubo, UpdateVerificationPayload, VerificationRequirement } from '../state/Radom'
import User from '../state/User'
import { RADOM_COLORS } from '../util/Constants'
import { errorToast, highlightInvalidFields, extractErrorMessage, successToast } from '../util/Util'
import BusinessAddressView from './Verify/BusinessAddress'
import Documents from './Verify/Documents'
import Ubos, { isUboComplete, UboForm } from './Verify/Ubos'
import LegalAndTaxDetailsView from './Verify/LegalAndTaxDetails'
import Review from './Verify/Review'
import AccountUsage from './Verify/AccountUsage'
import { AccountServicesSelection } from './Verify/AccountServicesSelection'
import BusinessBasics from './Verify/BusinessBasics'
import Spinner from '../components/Spinner'

interface LocalStorageDraft {
  accountUsageForm: Omit<AccountUsageFormState, 'logoFile'>
  businessBasicsForm: BusinessBasicsTypes
  businessAddressForm: Omit<BusinessAddressFormState, 'proofOfAddressFile'>
  legalAndTaxDetailsForm: Omit<LegalAndTaxDetailsFormState, 'proofOfWealthFile'>
  ubo: Omit<UboFormState, 'govIdImageFile' | 'proofOfAddressFile'>
  savedAt: string
}

const getStorageKey = (orgId: string): string => `verify_form_draft_${orgId}`

const saveToLocalStorage = (data: Omit<LocalStorageDraft, 'savedAt'>, orgId?: string): void => {
  if (!orgId) return
  try {
    const dataWithTimestamp: LocalStorageDraft = { ...data, savedAt: new Date().toISOString() }
    localStorage.setItem(getStorageKey(orgId), JSON.stringify(dataWithTimestamp))
  } catch (e) {
    console.error('Failed to save to localStorage', e)
  }
}

const loadFromLocalStorage = (orgId?: string, adminUpdatedAt?: string): LocalStorageDraft | null => {
  if (!orgId) return null
  try {
    const data = localStorage.getItem(getStorageKey(orgId))
    if (!data) return null
    const parsed: LocalStorageDraft = JSON.parse(data)
    if (adminUpdatedAt && parsed.savedAt) {
      const adminTime = new Date(adminUpdatedAt).getTime()
      const savedTime = new Date(parsed.savedAt).getTime()
      if (adminTime > savedTime) {
        clearLocalStorage(orgId)
        return null
      }
    }
    return parsed
  } catch (e) {
    console.error('Failed to load from localStorage', e)
    return null
  }
}

const clearLocalStorage = (orgId?: string): void => {
  if (!orgId) return
  try {
    localStorage.removeItem(getStorageKey(orgId))
  } catch (e) {
    console.error('Failed to clear localStorage', e)
  }
}

const getStepStorageKey = (orgId: string): string => `verify_current_step_${orgId}`

const saveStepToLocalStorage = (step: Steps, orgId?: string): void => {
  if (!orgId) return
  try {
    localStorage.setItem(getStepStorageKey(orgId), step)
  } catch (e) {
    console.error('Failed to save step to localStorage', e)
  }
}

const loadStepFromLocalStorage = (orgId?: string): Steps | null => {
  if (!orgId) return null
  try {
    const step = localStorage.getItem(getStepStorageKey(orgId))
    return step as Steps | null
  } catch (e) {
    console.error('Failed to load step from localStorage', e)
    return null
  }
}

const clearStepFromLocalStorage = (orgId?: string): void => {
  if (!orgId) return
  try {
    localStorage.removeItem(getStepStorageKey(orgId))
  } catch (e) {
    console.error('Failed to clear step from localStorage', e)
  }
}

enum Steps {
  accountUsage = 'Account Usage',
  businessBasics = 'Business Basics',
  businessAddress = 'Business Address',
  legalAndTaxDetails = 'Legal & Tax Details',
  // financialInformation = 'Financial Information',
  // regulatoryCompliance = 'Regulatory Compliance',
  documents = 'Documents',
  ubos = 'Ultimate Beneficial Owners',
  review = 'Review and submit'
}

interface AccountUsageFormState extends IAccountUsage {
  logoFile?: File
  logoUrl?: string
  lastUploadedLogoFileName?: string
}

interface BusinessAddressFormState extends BusinessAddress {
  proofOfAddressFile?: File
  proofOfAddressUrl?: string
  lastUploadedProofOfAddressFileName?: string
}

interface LegalAndTaxDetailsFormState extends LegalAndTaxDetails {
  proofOfWealthFile?: File
  proofOfWealthUrl?: string
  lastUploadedProofOfWealthFileName?: string
}

interface UboFormState extends Ubo {
  govIdImageFile?: File
  govIdImageUrl?: string
  lastUploadedGovIdImageFileName?: string
  proofOfAddressFile?: File
  proofOfAddressUrl?: string
  lastUploadedProofOfAddressFileName?: string
}

const DEFAULT_UBO: UboFormState = {
  firstName: '',
  lastName: '',
  birthDate: '',
  email: '',
  phone: '',
  taxIdentificationNumber: '',
  taxIdentificationNumberType: '',
  residentialAddress: {
    streetLine1: '',
    city: '',
    subdivision: '',
    postalCode: '',
    country: ''
  },
  relationshipEstablishedAt: '',
  govIdImageCountry: '',
  govIdImageType: '',
  hasOwnership: false,
  hasControl: false,
  isSigner: false,
  govIdImage: '',
  govIdImageFile: undefined,
  govIdImageUrl: undefined,
  lastUploadedGovIdImageFileName: undefined,
  proofOfAddress: '',
  proofOfAddressFile: undefined,
  proofOfAddressUrl: undefined,
  lastUploadedProofOfAddressFileName: undefined
}

const Navigation = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 40px;
  font-size: 16px;
  padding: 20px 0 20px 20px;
  border-bottom: 1px solid ${RADOM_COLORS.GRAY_DARK};
  position: sticky;
  top: 0;
  background: rgba(255, 255, 255, 0.9);
  z-index: 1;
`

const Verify = observer((): ReactElement => {
  const navigate = useNavigate()
  const serverDataLoadedRef = useRef(false)
  const skipSaveRef = useRef(false)

  useEffect(() => {
    NavbarController.setFullscreen(true)
    return () => NavbarController.setFullscreen(false)
  }, [])

  const [serverVerification, setVerification] = useState<IVerification | null | undefined>(undefined)
  const [requirements, setRequirements] = useState<VerificationRequirement[]>([])
  const [step, setStep] = useState(Steps.accountUsage)
  const [isDirty, setIsDirty] = useState({
    [Steps.accountUsage]: false,
    [Steps.businessBasics]: false,
    [Steps.businessAddress]: false,
    // [Steps.legalAndTaxDetails]: false,
    // [Steps.financialInformation]: false,
    // [Steps.regulatoryCompliance]: false,
    [Steps.ubos]: false,
    [Steps.documents]: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [accountUsageForm, updateAccountUsageForm] = useReducer((
    prev: AccountUsageFormState, next: Partial<AccountUsageFormState>) => { return { ...prev, ...next } }, {
    accountServices: AccountServicesType.crypto_and_fiat,
    productsUsage: '',
    monthlyVolume: '',
    paymentCounterparties: '',
    displayName: '',
    logo: '',
    logoFile: undefined,
    logoUrl: undefined,
    discoverRadom: ''
  })
  const accountType = useMemo(() => {
    return accountUsageForm.accountServices
  }, [accountUsageForm])
  const [businessBasicsForm, updateBusinessBasicsForm] = useReducer((
    prev: BusinessBasicsTypes, next: Partial<BusinessBasicsTypes>) => { return { ...prev, ...next } }, {
    name: '',
    description: '',
    businessType: BusinessTypes.cooperative,
    businessActivityPeriod: '',
    businessIndustry: [],
    website: '',
    email: ''
  })
  const [businessAddressForm, updateBusinessAddressForm] = useState<BusinessAddressFormState>({
    registeredAddress: {
      streetLine1: '',
      city: '',
      subdivision: '',
      country: ''
    },
    physicalAddress: {
      streetLine1: '',
      city: '',
      subdivision: '',
      country: ''
    },
    proofOfAddress: ''
  })
  const [legalAndTaxDetailsForm, updateLegalAndTaxDetailsForm] = useState<LegalAndTaxDetailsFormState>({
    expectedCryptoActivity: '',
    expectedFiatActivity: '',
    expectedOriginsOfFunds: '',
    sourceOfFunds: '',
    taxIdentificationNumberType: '',
    taxIdentificationNumber: '',
    proofOfWealth: ''

  })
  const [ubo, updateUbo] = useReducer(
    (prev: UboFormState, next: Partial<UboFormState>) => { return { ...prev, ...next } }, DEFAULT_UBO)
  const [ubos, setUbos] = useState<UboFormState[]>([])
  const [editUboIndex, setEditUboIndex] = useState(-1)
  const [documentsForm, updateDocumentsForm] = useReducer((prev, next) => { return { ...prev, ...next } }, {
    formationDocument: undefined,
    formationDocumentFile: undefined,
    lastUploadedFormationDocumentFileName: undefined,
    ownershipDocument: undefined,
    ownershipDocumentFile: undefined,
    lastUploadedOwnershipDocumentFileName: undefined,
    activityDocument: undefined,
    activityDocumentFile: undefined,
    lastUploadedActivityDocumentFileName: undefined,
    additionalDocument: undefined,
    additionalDocumentFile: undefined,
    lastUploadedAdditionalDocumentFileName: undefined,
    formationOrOwnershipDocument: undefined,
    formationOrOwnershipDocumentFile: undefined,
    lastUploadedFormationOrOwnershipDocumentFileName: undefined
  })

  const isStepDirty = useMemo(() => isDirty[step], [isDirty, step])
  const isAccountUsageValid = useMemo(() => {
    return !!accountUsageForm.accountServices &&
      !!accountUsageForm.productsUsage &&
      !!accountUsageForm.monthlyVolume &&
      (accountUsageForm.accountServices ===
        AccountServicesType.crypto_only || !!accountUsageForm.paymentCounterparties) &&
      !!accountUsageForm.displayName
  }, [accountUsageForm])
  const isBusinessBasicsValid = useMemo(() => {
    return !!businessBasicsForm.name &&
      (accountType === AccountServicesType.crypto_only || !!businessBasicsForm.businessActivityPeriod) &&
      !!businessBasicsForm.description &&
      !!businessBasicsForm.businessType &&
      !!businessBasicsForm.website &&
      !!businessBasicsForm.email
  }, [businessBasicsForm, accountType])
  const steps = useMemo(() => {
    const allSteps = Object.entries(Steps).map(([key, value]) => ({ id: key, label: value }))
    if (accountType === AccountServicesType.crypto_only) {
      return allSteps.filter(s => s.id !== 'businessAddress' && s.id !== 'legalAndTaxDetails')
    }
    return allSteps
  }, [accountType])

  const isBusinessAddressValid = useMemo(() => {
    if (accountType === AccountServicesType.crypto_only) {
      return !!businessAddressForm.registeredAddress.streetLine1 && !!businessAddressForm.registeredAddress.city &&
             !!businessAddressForm.registeredAddress.country
    }
    const phys = businessAddressForm.physicalAddress
    return !!phys.streetLine1 && !!phys.city && !!phys.country &&
      (!!businessAddressForm.proofOfAddress || !!businessAddressForm.proofOfAddressFile)
  }, [businessAddressForm, accountType])

  const isLegalAndTaxValid = useMemo(() => {
    return accountType === AccountServicesType.crypto_and_fiat
      ? (!!legalAndTaxDetailsForm.expectedCryptoActivity &&
      !!legalAndTaxDetailsForm.expectedFiatActivity &&
      !!legalAndTaxDetailsForm.expectedOriginsOfFunds &&
      !!legalAndTaxDetailsForm.sourceOfFunds &&
      !!legalAndTaxDetailsForm.taxIdentificationNumberType &&
      !!legalAndTaxDetailsForm.taxIdentificationNumber &&
      (!!legalAndTaxDetailsForm.proofOfWealth || !!legalAndTaxDetailsForm.proofOfWealthFile))
      : (!!legalAndTaxDetailsForm.expectedCryptoActivity && !!legalAndTaxDetailsForm.expectedOriginsOfFunds)
  }, [legalAndTaxDetailsForm])

  const isDocumentsValid = useMemo(() => {
    if (accountType === AccountServicesType.crypto_and_fiat) {
      return (
        (!!documentsForm.formationDocument || !!documentsForm.formationDocumentFile) &&
        (!!documentsForm.ownershipDocument || !!documentsForm.ownershipDocumentFile) &&
        (!!documentsForm.activityDocument || !!documentsForm.activityDocumentFile)
      )
    } else {
      return (!!documentsForm.formationOrOwnershipDocument || !!documentsForm.formationOrOwnershipDocumentFile)
    }
  }, [documentsForm, accountType])

  const isUbosValid = useMemo(() => {
    if (ubos.length === 0) return false
    return ubos.every(u => isUboComplete(u, accountType))
  }, [ubos, accountType])

  const canSubmit = useMemo(() => {
    const baseValidation = isAccountUsageValid && isBusinessBasicsValid && isDocumentsValid && isUbosValid
    if (accountType === AccountServicesType.crypto_only) {
      return baseValidation
    }
    return baseValidation && isBusinessAddressValid && isLegalAndTaxValid
  },
  [
    isAccountUsageValid,
    isBusinessBasicsValid,
    isBusinessAddressValid,
    isLegalAndTaxValid,
    isDocumentsValid,
    isUbosValid,
    accountType
  ])

  const stepCompleted = useMemo(() => ({
    [Steps.accountUsage]: isAccountUsageValid,
    [Steps.businessBasics]: isBusinessBasicsValid,
    [Steps.businessAddress]: accountType === AccountServicesType.crypto_only ? true : isBusinessAddressValid,
    [Steps.legalAndTaxDetails]: accountType === AccountServicesType.crypto_only ? true : isLegalAndTaxValid,
    [Steps.documents]: isDocumentsValid,
    [Steps.ubos]: isUbosValid,
    [Steps.review]: canSubmit
  }), [
    isAccountUsageValid,
    isBusinessBasicsValid,
    isBusinessAddressValid,
    isLegalAndTaxValid,
    isDocumentsValid,
    isUbosValid,
    canSubmit,
    accountType
  ])

  useEffect(() => {
    Radom.getVerification().then((v) => {
      const verification: Partial<IVerification> = v || {}
      setRequirements(verification?.requirements ?? [])
      setVerification(v ?? null)
      const currentOrgId = User.organization?.organizationId
      const draft = loadFromLocalStorage(currentOrgId, verification.adminUpdatedAt)
      updateAccountUsageForm({
        accountServices: draft?.accountUsageForm?.accountServices ?? verification.accountServices,
        productsUsage: draft?.accountUsageForm?.productsUsage ?? verification.productsUsage,
        monthlyVolume: draft?.accountUsageForm?.monthlyVolume ?? verification.monthlyVolume,
        paymentCounterparties: draft?.accountUsageForm?.paymentCounterparties ?? verification.paymentCounterparties,
        displayName: draft?.accountUsageForm?.displayName ?? verification.displayName,
        logo: verification.logo,
        logoUrl: verification.logoUrl,
        discoverRadom: draft?.accountUsageForm?.discoverRadom ?? verification.discoverRadom
      })

      updateBusinessBasicsForm({
        name: draft?.businessBasicsForm?.name ?? verification.name,
        description: draft?.businessBasicsForm?.description ?? verification.description,
        businessType: draft?.businessBasicsForm?.businessType ?? verification.businessType,
        businessIndustry: draft?.businessBasicsForm?.businessIndustry ?? verification.businessIndustry,
        businessActivityPeriod: draft?.businessBasicsForm?.businessActivityPeriod ??
          verification.businessActivityPeriod,
        website: draft?.businessBasicsForm?.website ?? verification.website,
        email: draft?.businessBasicsForm?.email ?? verification.email
      })

      updateBusinessAddressForm({
        registeredAddress: draft?.businessAddressForm?.registeredAddress ?? verification.registeredAddress ?? { streetLine1: '', city: '', country: '' },
        physicalAddress: draft?.businessAddressForm?.physicalAddress ?? verification.physicalAddress ?? { streetLine1: '', city: '', country: '' },
        proofOfAddress: verification.proofOfAddress || '',
        proofOfAddressUrl: verification.proofOfAddressUrl
      })

      updateLegalAndTaxDetailsForm({
        expectedCryptoActivity: draft?.legalAndTaxDetailsForm?.expectedCryptoActivity ?? verification.expectedCryptoActivity ?? '',
        expectedFiatActivity: draft?.legalAndTaxDetailsForm?.expectedFiatActivity ?? verification.expectedFiatActivity ?? '',
        expectedOriginsOfFunds: draft?.legalAndTaxDetailsForm?.expectedOriginsOfFunds ?? verification.expectedOriginsOfFunds ?? '',
        sourceOfFunds: draft?.legalAndTaxDetailsForm?.sourceOfFunds ?? verification.sourceOfFunds ?? '',
        taxIdentificationNumberType: draft?.legalAndTaxDetailsForm?.taxIdentificationNumberType ?? verification.taxIdentificationNumberType ?? '',
        taxIdentificationNumber: draft?.legalAndTaxDetailsForm?.taxIdentificationNumber ?? verification.taxIdentificationNumber ?? '',
        proofOfWealth: verification.proofOfWealth || '',
        proofOfWealthUrl: verification.proofOfWealthUrl
      })

      updateDocumentsForm({
        formationDocument: verification.formationDocument,
        formationDocumentUrl: verification.formationDocumentUrl,
        ownershipDocument: verification.ownershipDocument,
        ownershipDocumentUrl: verification.ownershipDocumentUrl,
        activityDocument: verification.activityDocument,
        activityDocumentUrl: verification.activityDocumentUrl,
        additionalDocument: verification.additionalDocument,
        additionalDocumentUrl: verification.additionalDocumentUrl,
        formationOrOwnershipDocument: verification.formationOrOwnershipDocument,
        formationOrOwnershipDocumentUrl: verification.formationOrOwnershipDocumentUrl
      })

      setUbos(verification.associatedPersons ?? [])

      const savedStep = loadStepFromLocalStorage(currentOrgId)
      if (savedStep) {
        setStep(savedStep)
      }

      if (draft?.ubo) {
        updateUbo(draft.ubo)
      }

      serverDataLoadedRef.current = true
    })
  }, [])

  useEffect(() => {
    if (!serverDataLoadedRef.current) return

    saveStepToLocalStorage(step, User.organization?.organizationId)

    if (skipSaveRef.current) {
      skipSaveRef.current = false
      return
    }

    const draftData: Omit<LocalStorageDraft, 'savedAt'> = {
      accountUsageForm: {
        accountServices: accountUsageForm.accountServices,
        productsUsage: accountUsageForm.productsUsage,
        monthlyVolume: accountUsageForm.monthlyVolume,
        paymentCounterparties: accountUsageForm.paymentCounterparties,
        displayName: accountUsageForm.displayName,
        logo: accountUsageForm.logo,
        logoUrl: accountUsageForm.logoUrl,
        discoverRadom: accountUsageForm.discoverRadom
      },
      businessBasicsForm,
      businessAddressForm: {
        registeredAddress: businessAddressForm.registeredAddress,
        physicalAddress: businessAddressForm.physicalAddress,
        proofOfAddress: businessAddressForm.proofOfAddress,
        proofOfAddressUrl: businessAddressForm.proofOfAddressUrl
      },
      legalAndTaxDetailsForm: {
        expectedCryptoActivity: legalAndTaxDetailsForm.expectedCryptoActivity,
        expectedFiatActivity: legalAndTaxDetailsForm.expectedFiatActivity,
        expectedOriginsOfFunds: legalAndTaxDetailsForm.expectedOriginsOfFunds,
        sourceOfFunds: legalAndTaxDetailsForm.sourceOfFunds,
        taxIdentificationNumberType: legalAndTaxDetailsForm.taxIdentificationNumberType,
        taxIdentificationNumber: legalAndTaxDetailsForm.taxIdentificationNumber,
        proofOfWealth: legalAndTaxDetailsForm.proofOfWealth,
        proofOfWealthUrl: legalAndTaxDetailsForm.proofOfWealthUrl
      },
      ubo: {
        firstName: ubo.firstName,
        lastName: ubo.lastName,
        birthDate: ubo.birthDate,
        email: ubo.email,
        phone: ubo.phone,
        taxIdentificationNumber: ubo.taxIdentificationNumber,
        taxIdentificationNumberType: ubo.taxIdentificationNumberType,
        residentialAddress: ubo.residentialAddress,
        relationshipEstablishedAt: ubo.relationshipEstablishedAt,
        govIdImageCountry: ubo.govIdImageCountry,
        govIdImageType: ubo.govIdImageType,
        hasOwnership: ubo.hasOwnership,
        hasControl: ubo.hasControl,
        isSigner: ubo.isSigner,
        govIdImage: ubo.govIdImage,
        govIdImageUrl: ubo.govIdImageUrl,
        lastUploadedGovIdImageFileName: ubo.lastUploadedGovIdImageFileName,
        proofOfAddress: ubo.proofOfAddress,
        proofOfAddressUrl: ubo.proofOfAddressUrl,
        lastUploadedProofOfAddressFileName: ubo.lastUploadedProofOfAddressFileName,
        govIdExpirationDate: ubo.govIdExpirationDate
      }
    }

    saveToLocalStorage(draftData, User.organization?.organizationId)
  }, [step, accountUsageForm, businessBasicsForm, businessAddressForm, legalAndTaxDetailsForm, ubo])

  const onUpdate = (value: any): void => {
    setIsDirty({ ...isDirty, [step]: true })

    switch (step) {
    case Steps.accountUsage:
      updateAccountUsageForm(value)
      break

    case Steps.businessBasics:
      updateBusinessBasicsForm(value)
      break

    case Steps.businessAddress:
      updateBusinessAddressForm(value)
      break

    case Steps.legalAndTaxDetails:
      updateLegalAndTaxDetailsForm(value)
      break

      // case Steps.financialInformation:
      //   updateFinancialInformationForm(value)
      //   break

      // case Steps.regulatoryCompliance:
      //   updateRegulatoryComplianceForm(value)
      //   break

    case Steps.ubos:
      updateUbo(value)
      break

    case Steps.documents:
      updateDocumentsForm(value)
      break
    }
  }
  const getStepForField = (fieldId: string): Steps | null => {
    if (['accountServices', 'productsUsage', 'monthlyVolume', 'paymentCounterparties', 'displayName', 'logo', 'discoverRadom'].some(f => fieldId.startsWith(f))) return Steps.accountUsage
    if (['name', 'description', 'businessType', 'businessIndustry', 'businessActivityPeriod', 'website', 'email'].some(f => fieldId.startsWith(f))) return Steps.businessBasics
    if (['registeredAddress', 'physicalAddress', 'proofOfAddress'].some(f => fieldId.startsWith(f))) return Steps.businessAddress
    if (['expectedCryptoActivity', 'expectedFiatActivity', 'expectedOriginsOfFunds', 'sourceOfFunds', 'taxIdentificationNumber', 'proofOfWealth'].some(f => fieldId.startsWith(f))) return Steps.legalAndTaxDetails
    if (['formationDocument', 'ownershipDocument', 'activityDocument', 'additionalDocument', 'formationOrOwnershipDocument'].some(f => fieldId.startsWith(f))) return Steps.documents
    if (fieldId.startsWith('associatedPersons')) return Steps.ubos
    return null
  }

  const stepErrors = useMemo(() => {
    const errors: Record<string, boolean> = {}
    requirements.forEach(req => {
      const step = getStepForField(req.fieldId)
      if (step) {
        errors[step] = true
      }
    })
    return errors
  }, [requirements])

  const getError = (fieldId: string): string | undefined => requirements.find(r => r.fieldId === fieldId)?.comment

  const updateUbos = async (ubos: UboForm[]): Promise<void> => {
    try {
      setIsSubmitting(true)
      const payload: Ubo[] = []
      const updatedUbos: UboForm[] = []

      for (const ubo of ubos) {
        let govIdImage = ubo.govIdImage
        let proofOfAddress = ubo.proofOfAddress
        let lastUploadedGovIdImageFileName = ubo.lastUploadedGovIdImageFileName
        let lastUploadedProofOfAddressFileName = ubo.lastUploadedProofOfAddressFileName

        if (ubo.govIdImageFile &&
            ubo.govIdImageFile.name !== ubo.lastUploadedGovIdImageFileName) {
          const formData = new FormData()
          formData.append('type', ubo.govIdImageFile?.type)
          formData.append('filename', ubo.govIdImageFile?.name)
          formData.append('content', ubo.govIdImageFile as Blob)

          govIdImage = await Radom.uploadVerificationImage(formData)
          lastUploadedGovIdImageFileName = ubo.govIdImageFile.name
        }

        if (ubo.proofOfAddressFile &&
            ubo.proofOfAddressFile.name !== ubo.lastUploadedProofOfAddressFileName) {
          const formData = new FormData()
          formData.append('type', ubo.proofOfAddressFile.type)
          formData.append('filename', ubo.proofOfAddressFile.name)
          formData.append('content', ubo.proofOfAddressFile)

          proofOfAddress = await Radom.uploadVerificationImage(formData)
          lastUploadedProofOfAddressFileName = ubo.proofOfAddressFile.name
        }

        updatedUbos.push({
          ...ubo,
          govIdImage,
          proofOfAddress,
          lastUploadedGovIdImageFileName,
          lastUploadedProofOfAddressFileName
        })

        payload.push({
          firstName: ubo.firstName,
          lastName: ubo.lastName,
          birthDate: ubo.birthDate,
          email: ubo.email,
          phone: ubo.phone,
          residentialAddress: ubo.residentialAddress,
          taxIdentificationNumberType: ubo.taxIdentificationNumberType || undefined,
          taxIdentificationNumber: ubo.taxIdentificationNumber || undefined,
          hasOwnership: ubo.hasOwnership,
          hasControl: ubo.hasControl,
          isSigner: ubo.isSigner,
          title: ubo.title || undefined,
          relationshipEstablishedAt: ubo.relationshipEstablishedAt || undefined,
          govIdImageType: ubo.govIdImageType,
          govIdImageCountry: ubo.govIdImageCountry,
          govIdImage,
          proofOfAddress,
          govIdExpirationDate: ubo.govIdExpirationDate
        })
      }

      await Radom.updateVerification(payload)

      setUbos(updatedUbos)

      setIsSubmitting(false)
      setIsDirty({ ...isDirty, [step]: true })
    } catch (error) {
      setIsSubmitting(false)
      errorToast(extractErrorMessage(error, 'Failed to update associated persons'))
      throw error
    }
  }

  const onSubmit = async (payload: UpdateVerificationPayload, step: Steps): Promise<void> => {
    try {
      if (isStepDirty) {
        setIsSubmitting(true)
        await Radom.updateVerification(payload)
        setIsSubmitting(false)
        setIsDirty({ ...isDirty, [step]: false })
      }
      skipSaveRef.current = true
      clearLocalStorage(User.organization?.organizationId)

      setStep(step)
    } catch (error) {
      errorToast(extractErrorMessage(error, 'Failed to update verification'))
      setIsSubmitting(false)
      setIsDirty({ ...isDirty, [step]: true })
    }
  }

  const renderStep = (): ReactElement => {
    switch (step) {
    case Steps.accountUsage:
      return <AccountUsage
        state={accountUsageForm}
        getError={getError}
        isSubmitting={isSubmitting}
        onUpdate={onUpdate}
        onSubmit={async () => {
          let logo = accountUsageForm.logo

          if (accountUsageForm.logoFile &&
              accountUsageForm.logoFile.name !== accountUsageForm.lastUploadedLogoFileName) {
            setIsSubmitting(true)
            const formData = new FormData()
            formData.append('type', accountUsageForm.logoFile.type)
            formData.append('filename', accountUsageForm.logoFile.name)
            formData.append('content', accountUsageForm.logoFile)

            try {
              logo = await Radom.uploadVerificationImage(formData)
              updateAccountUsageForm({ logo, lastUploadedLogoFileName: accountUsageForm.logoFile.name })
            } catch (err) {
              errorToast(extractErrorMessage(err, 'Failed to upload logo'))
              setIsSubmitting(false)
              return
            }
          }

          try {
            const organizationId = User.organization?.organizationId

            if (organizationId) {
              const profilePayload: any = {
                organizationId,
                name: accountUsageForm.displayName
              }

              if (accountUsageForm.logoFile) {
                const imageBuffer = await accountUsageForm.logoFile.arrayBuffer()
                profilePayload.imageBytes = Array.from(new Uint8Array(imageBuffer))
              }

              await Radom.updateSellerProfile(profilePayload)
            }
          } catch (error) {
            console.error('Failed to sync seller profile', error)
          }

          await onSubmit({
            accountServices: accountUsageForm.accountServices,
            productsUsage: accountUsageForm.productsUsage,
            monthlyVolume: accountUsageForm.monthlyVolume,
            paymentCounterparties: accountUsageForm.paymentCounterparties,
            displayName: accountUsageForm.displayName,
            logo,
            discoverRadom: accountUsageForm.discoverRadom
          }, Steps.businessBasics)
        }}
      />
    case Steps.businessBasics:
      return <BusinessBasics
        accountServices={accountType}
        getError={getError}
        state={businessBasicsForm}
        isSubmitting={isSubmitting}
        onUpdate={onUpdate}
        onSubmit={async () => {
          const nextStep = accountType === AccountServicesType.crypto_only ? Steps.documents : Steps.businessAddress
          await onSubmit(businessBasicsForm, nextStep)
        }}
      />

    case Steps.businessAddress:
      return <BusinessAddressView
        getError={getError}
        accountType={accountType}
        state={businessAddressForm}
        isSubmitting={isSubmitting}
        onUpdate={onUpdate}
        onSubmit={async () => {
          let proofOfAddress = businessAddressForm.proofOfAddress

          if (businessAddressForm.proofOfAddressFile &&
              businessAddressForm.proofOfAddressFile.name !==
                businessAddressForm.lastUploadedProofOfAddressFileName) {
            setIsSubmitting(true)
            const formData = new FormData()
            formData.append('type', businessAddressForm.proofOfAddressFile.type)
            formData.append('filename', businessAddressForm.proofOfAddressFile.name)
            formData.append('content', businessAddressForm.proofOfAddressFile)

            try {
              proofOfAddress = await Radom.uploadVerificationImage(formData)
              updateBusinessAddressForm(prev => ({
                ...prev,
                proofOfAddress,
                lastUploadedProofOfAddressFileName: businessAddressForm.proofOfAddressFile?.name
              }))
            } catch (err) {
              errorToast(extractErrorMessage(err, 'Failed to upload verification data'))
              setIsSubmitting(false)
              return
            }
          }
          await onSubmit({
            registeredAddress: businessAddressForm.registeredAddress,
            physicalAddress: businessAddressForm.physicalAddress,
            proofOfAddress
          }, Steps.legalAndTaxDetails)
        }}
      />

    case Steps.legalAndTaxDetails:
      return <LegalAndTaxDetailsView
        accountType={accountType}
        countryCode={businessAddressForm.registeredAddress.country}
        state={legalAndTaxDetailsForm}
        getError={getError}
        isSubmitting={isSubmitting}
        onUpdate={onUpdate}
        onSubmit={async () => {
          let proofOfWealth = legalAndTaxDetailsForm.proofOfWealth

          if (legalAndTaxDetailsForm.proofOfWealthFile &&
              legalAndTaxDetailsForm.proofOfWealthFile.name !==
                legalAndTaxDetailsForm.lastUploadedProofOfWealthFileName) {
            setIsSubmitting(true)
            const formData = new FormData()
            formData.append('type', legalAndTaxDetailsForm.proofOfWealthFile.type)
            formData.append('filename', legalAndTaxDetailsForm.proofOfWealthFile.name)
            formData.append('content', legalAndTaxDetailsForm.proofOfWealthFile)

            try {
              proofOfWealth = await Radom.uploadVerificationImage(formData)
              updateLegalAndTaxDetailsForm(prev => ({
                ...prev,
                proofOfWealth,
                lastUploadedProofOfWealthFileName: legalAndTaxDetailsForm.proofOfWealthFile?.name
              }))
            } catch (err) {
              errorToast(extractErrorMessage(err, 'Failed to upload verification data'))
              setIsSubmitting(false)
              return
            }
          }

          const {
            proofOfWealthFile,
            proofOfWealthUrl,
            ...cleanLegalAndTaxDetails
          } = legalAndTaxDetailsForm

          await onSubmit({
            ...cleanLegalAndTaxDetails,
            proofOfWealth
          }, Steps.documents)
        }}
      />

      // case Steps.financialInformation:
      //   return <FinancialInformation
      //     state={financialInformationForm}
      //     isSubmitting={isSubmitting}
      //     onUpdate={onUpdate}
      //     onSubmit={async () => {
      //       let payload = {
      //         sourceOfFunds: financialInformationForm.sourceOfFunds,
      //         statementOfFunds: financialInformationForm.statementOfFunds
      //       }

      //       if (isStepDirty) {
      //         setIsSubmitting(true)
      //         const formData = new FormData()
      //         formData.append('type', 'Document')
      //         formData.append('content', financialInformationForm.statementOfFundsFile)

      //         const statementOfFunds = await Radom.uploadVerificationImage(verification?.id as string, formData)

      //         payload = {
      //           sourceOfFunds: financialInformationForm.sourceOfFunds,
      //           statementOfFunds
      //         }
      //       }

      //       await onSubmit(payload, Steps.regulatoryCompliance)
      //     }}
      //   />

      // case Steps.regulatoryCompliance:
      //   return <RegulatoryCompliance
      //     state={regulatoryComplianceForm}
      //     isSubmitting={isSubmitting}
      //     onUpdate={onUpdate}
      //     onSubmit={async () => await onSubmit(regulatoryComplianceForm, Steps.ubos)}
      //   />

    case Steps.documents:
      return <Documents
        state={documentsForm}
        isSubmitting={isSubmitting}
        onUpdate={onUpdate}
        getError={getError}
        accountType={accountType}
        onSubmit={async () => {
          if (isStepDirty) {
            try {
              setIsSubmitting(true)
              const updates: any = {}

              if (accountType === AccountServicesType.crypto_and_fiat) {
                if (documentsForm.formationDocumentFile &&
                    documentsForm.formationDocumentFile.name !==
                      documentsForm.lastUploadedFormationDocumentFileName) {
                  const formationDocumentData = new FormData()
                  formationDocumentData.append('type', documentsForm.formationDocumentFile.type)
                  formationDocumentData.append('filename', documentsForm.formationDocumentFile.name)
                  formationDocumentData.append('content', documentsForm.formationDocumentFile)

                  const formationDocument = await Radom.uploadVerificationImage(formationDocumentData)
                  updateDocumentsForm({
                    formationDocument,
                    lastUploadedFormationDocumentFileName: documentsForm.formationDocumentFile.name
                  })
                  updates.formationDocument = formationDocument
                }

                if (documentsForm.ownershipDocumentFile &&
                    documentsForm.ownershipDocumentFile.name !==
                      documentsForm.lastUploadedOwnershipDocumentFileName) {
                  const ownershipDocumentData = new FormData()
                  ownershipDocumentData.append('type', documentsForm.ownershipDocumentFile.type)
                  ownershipDocumentData.append('filename', documentsForm.ownershipDocumentFile.name)
                  ownershipDocumentData.append('content', documentsForm.ownershipDocumentFile)

                  const ownershipDocument = await Radom.uploadVerificationImage(ownershipDocumentData)
                  updateDocumentsForm({
                    ownershipDocument,
                    lastUploadedOwnershipDocumentFileName: documentsForm.ownershipDocumentFile.name
                  })
                  updates.ownershipDocument = ownershipDocument
                }

                if (documentsForm.activityDocumentFile &&
                    documentsForm.activityDocumentFile.name !==
                      documentsForm.lastUploadedActivityDocumentFileName) {
                  const activityDocumentData = new FormData()
                  activityDocumentData.append('type', documentsForm.activityDocumentFile.type)
                  activityDocumentData.append('filename', documentsForm.activityDocumentFile.name)
                  activityDocumentData.append('content', documentsForm.activityDocumentFile)

                  const activityDocument = await Radom.uploadVerificationImage(activityDocumentData)
                  updateDocumentsForm({
                    activityDocument,
                    lastUploadedActivityDocumentFileName: documentsForm.activityDocumentFile.name
                  })
                  updates.activityDocument = activityDocument
                }

                if (documentsForm.additionalDocumentFile &&
                    documentsForm.additionalDocumentFile.name !==
                      documentsForm.lastUploadedAdditionalDocumentFileName) {
                  const additionalDocumentData = new FormData()
                  additionalDocumentData.append('type', documentsForm.additionalDocumentFile.type)
                  additionalDocumentData.append('filename', documentsForm.additionalDocumentFile.name)
                  additionalDocumentData.append('content', documentsForm.additionalDocumentFile)

                  const additionalDocument = await Radom.uploadVerificationImage(additionalDocumentData)
                  updateDocumentsForm({
                    additionalDocument,
                    lastUploadedAdditionalDocumentFileName: documentsForm.additionalDocumentFile.name
                  })
                  updates.additionalDocument = additionalDocument
                } else if (documentsForm.additionalDocument === '') {
                  updates.additionalDocument = undefined
                }
              } else {
                if (documentsForm.formationOrOwnershipDocumentFile &&
                    documentsForm.formationOrOwnershipDocumentFile.name !==
                      documentsForm.lastUploadedFormationOrOwnershipDocumentFileName) {
                  const formationOrOwnershipDocumentData = new FormData()
                  formationOrOwnershipDocumentData.append('type', documentsForm.formationOrOwnershipDocumentFile.type)
                  formationOrOwnershipDocumentData.append('filename',
                    documentsForm.formationOrOwnershipDocumentFile.name)
                  formationOrOwnershipDocumentData.append('content', documentsForm.formationOrOwnershipDocumentFile)

                  const formationOrOwnershipDocument = await Radom.uploadVerificationImage(
                    formationOrOwnershipDocumentData)
                  updateDocumentsForm({
                    formationOrOwnershipDocument,
                    lastUploadedFormationOrOwnershipDocumentFileName:
                      documentsForm.formationOrOwnershipDocumentFile.name
                  })
                  updates.formationOrOwnershipDocument = formationOrOwnershipDocument
                }

                if (documentsForm.additionalDocumentFile &&
                    documentsForm.additionalDocumentFile.name !==
                      documentsForm.lastUploadedAdditionalDocumentFileName) {
                  const additionalDocumentData = new FormData()
                  additionalDocumentData.append('type', documentsForm.additionalDocumentFile.type)
                  additionalDocumentData.append('filename', documentsForm.additionalDocumentFile.name)
                  additionalDocumentData.append('content', documentsForm.additionalDocumentFile)

                  const additionalDocument = await Radom.uploadVerificationImage(additionalDocumentData)
                  updateDocumentsForm({
                    additionalDocument,
                    lastUploadedAdditionalDocumentFileName: documentsForm.additionalDocumentFile.name
                  })
                  updates.additionalDocument = additionalDocument
                } else if (documentsForm.additionalDocument === '') {
                  updates.additionalDocument = undefined
                }
              }

              if (Object.keys(updates).length > 0) {
                await onSubmit(updates, Steps.ubos)
              } else {
                setStep(Steps.ubos)
                setIsSubmitting(false)
              }
            } catch (error) {
              errorToast(extractErrorMessage(error, 'Failed to upload documents'))
              setIsSubmitting(false)
              setIsDirty({ ...isDirty, [step]: false })
              updateDocumentsForm({
                formationDocumentFile: undefined,
                ownershipDocumentFile: undefined,
                activityDocumentFile: undefined,
                additionalDocumentFile: undefined,
                formationOrOwnershipDocumentFile: undefined
              })
            }
          } else {
            setStep(Steps.ubos)
          }
        }}
      />

    case Steps.ubos:
      return <Ubos
        accountType={accountType}
        state={{ ubos, ubo }}
        isSubmitting={isSubmitting}
        canSubmit={canSubmit}
        onUpdate={onUpdate}
        requirements={requirements}
        onAdd={async (ubo) => {
          let nextUbos: UboForm[] = []
          if (editUboIndex > -1) {
            nextUbos = [
              ...ubos.slice(0, editUboIndex),
              ubo,
              ...ubos.slice(editUboIndex + 1)
            ]
          } else {
            nextUbos = [...ubos, ubo]
          }

          try {
            await updateUbos(nextUbos)
            updateUbo(DEFAULT_UBO)
          } catch (err) {
            console.error(err)
            throw err
          }
        }}
        onEdit={(index) => {
          setEditUboIndex(index)
          updateUbo(ubos[index])
        }}
        onStopEdit={() => {
          setEditUboIndex(-1)
          updateUbo(DEFAULT_UBO)
        }}
        onRemove={async (index) => {
          const newUbos = ubos.slice()
          newUbos.splice(index, 1)
          await updateUbos(newUbos)
        }}
        onSubmit={async () => {
          setStep(Steps.review)
        }}
      />

    case Steps.review:
      return <Review
        canSubmit={canSubmit}
        onSubmit={async () => {
          try {
            await Radom.updateVerification()
            await User.getOrganization(true)
            skipSaveRef.current = true
            clearLocalStorage(User.organization?.organizationId)
            clearStepFromLocalStorage(User.organization?.organizationId)
            successToast('Verification submitted successfully')
            navigate('/')
          } catch (error) {
            errorToast(extractErrorMessage(error, 'Failed to submit verification'))
            setIsSubmitting(false)
          }
        }}
        isSubmitting={isSubmitting} />
    default:
      return <></>
    }
  }

  if (serverVerification === undefined) {
    return <Container style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, height: '100vh' }}>
      <Spinner />
    </Container>
  }

  if (serverVerification === null) {
    return <AccountServicesSelection onContinue={(id) => {
      updateAccountUsageForm({ accountServices: id })
      setVerification({} as any)
      setStep(Steps.accountUsage)
    }} />
  }

  const verificationStatus = User.organization?.verificationStatus
  if (verificationStatus === 'Verified' || verificationStatus === 'Rejected' || verificationStatus === 'PendingApproval') {
    return <Navigate to='/' replace />
  }

  return <Container style={{ display: 'block', padding: 0 }}>
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Navigation>
        <Chevron
          style={{
            fill: RADOM_COLORS.GRAY_DARKEST,
            height: '10px',
            width: '10px',
            transform: 'rotate(90deg)',
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}
        />
        <span>Verify organization</span>
      </Navigation>
      <div style={{ display: 'flex', padding: '0 40px', gap: 240, width: '100%' }}>
        <ProgressIndicator
          steps={steps}
          currentStep={step}
          completedSteps={steps.filter(s => stepCompleted[s.label]).map(s => s.label)}
          canNavigateToStep={(stepId) => {
            const targetIndex = steps.findIndex(s => s.id === stepId)
            const currentIndex = steps.findIndex(s => s.label === step)
            if (targetIndex <= currentIndex) {
              return true
            }
            return steps.slice(0, targetIndex).every(s => stepCompleted[s.label])
          }}
          errorSteps={Object.keys(stepErrors).filter(k => stepErrors[k])}
          onStepClick={(stepId) => {
            const canMove = steps.slice(0, steps.findIndex(s => s.id === stepId)).every(s => stepCompleted[s.label])
            if (canMove || steps.findIndex(s => s.id === stepId) <= steps.findIndex(s => s.label === step)) {
              setStep(Steps[stepId])
            } else {
              highlightInvalidFields()
            }
          }}
        />
        <div style={{ width: step === Steps.accountUsage ? '829px' : '500px' }}>
          {renderStep()}
        </div>
      </div>
    </div>
  </Container>
})

export default Verify
